<template>
  <div class="v2026-mtm-anchor" @keydown.esc="onEsc" @focusout="onFocusOut">
    <!-- Same class as the b-dropdown toggle it replaces, so the closed box stays pixel-identical. -->
    <button type="button"
            ref="triggerBtn"
            class="v2026-keylad"
            aria-haspopup="listbox"
            :aria-expanded="open ? 'true' : 'false'"
            :aria-controls="panelId"
            @click.prevent="toggle"
            @keydown.down.prevent="onTriggerArrow('first')"
            @keydown.up.prevent="onTriggerArrow('last')">
      คีย์ลัด
      <i class="fas fa-chevron-up v2026-chev" aria-hidden="true"></i>
    </button>

    <!-- :renderKey + the scoped slot's :data-rk are one fix: without both, Vue 2.7 treats the
         slot as stable and freezes the rows. v-show, never v-if — v-if would rebuild the
         vue-plyr instance and rebind every v-lazy image on each open. -->
    <OverlayCard :id="panelId"
                 class="v2026-mtm-pos"
                 :open="open"
                 v-show="open"
                 :ignore="triggerEl"
                 :renderKey="renderKey"
                 role="region"
                 aria-label="คีย์ลัด"
                 @close="hide()">
      <template #default="rk">
        <div class="v2026-mtm-head" :data-rk="rk.dirty">
          <div class="v2026-mtm-search">
            <i class="fas fa-search v2026-mtm-search__ico" aria-hidden="true"></i>
            <input ref="searchInput"
                   v-model.trim="query"
                   type="text"
                   class="v2026-mtm-search__input"
                   role="combobox"
                   aria-expanded="true"
                   aria-autocomplete="list"
                   aria-label="ค้นหาคำ"
                   placeholder="ค้นหาคำ"
                   :aria-controls="listId"
                   :aria-activedescendant="activeOptionId"
                   @input="onSearchInput"
                   @keydown="onSearchKeydown">
          </div>
          <RouterLink to="/settings/message-template"
                      class="v2026-mtm-setting"
                      @click.native="hide()">
            <span class="v2026-mtm-setting__ico" aria-hidden="true"
                  :style="{ '-webkit-mask-image': 'url(' + settingsIcon + ')', 'mask-image': 'url(' + settingsIcon + ')' }"></span>
            <span>ตั้งค่า</span>
          </RouterLink>
        </div>

        <!-- THE single scroller: pagination, the v-lazy scroll parent and the height clamp all hang
             off this element. Never add a second overflow ancestor between here and the images. -->
        <div :id="listId"
             ref="list"
             class="v2026-mtm-list"
             role="listbox"
             tabindex="-1"
             aria-label="คีย์ลัด"
             @scroll.passive="onScroll"
             @mousedown="onListMousedown">
          <div v-for="group in groups"
               :key="group.key"
               class="v2026-mtm-group"
               role="group"
               :aria-label="group.name">
            <div class="v2026-mtm-header" :title="group.name" lang="th">{{ group.name }}</div>

            <!-- :key must stay stable. Baking activeIndex in would re-create the row, and with it a
                 second vue-plyr instance, on every arrow press. -->
            <div v-for="row in group.rows"
                 :key="row.key"
                 :id="row.id"
                 :data-idx="row.idx"
                 class="v2026-mtm-row"
                 :class="{ 'is-active': row.idx === activeIndex }"
                 role="option"
                 :aria-selected="row.idx === activeIndex ? 'true' : 'false'"
                 :aria-label="row.label"
                 @click="onPick(row)">
              <span class="v2026-mtm-key">/{{ row.item.key }}</span>

              <div v-if="row.item.topic" class="v2026-mtm-topic" :title="row.item.topic" lang="th">{{ row.item.topic }}</div>

              <div class="v2026-mtm-body" lang="th">
                <template v-if="row.item.type === 'text'">
                  <span v-if="row.item.message">{{ row.item.message | shortText(70) }}</span>
                  <span v-else>&nbsp;</span>
                </template>
                <div v-else-if="row.item.type === 'image'" class="v2026-mtm-strip">
                  <img v-for="(image, i) in row.item.images"
                       :key="i"
                       :src="image.url"
                       class="v2026-mtm-strip__img"
                       height="50"
                       alt="">
                </div>
              </div>

              <div v-if="row.item.attachment" class="v2026-mtm-att">
                <div v-if="getFileExtension(row.item.attachment) === 'mp4'" class="v2026-mtm-att__video">
                  <VuePlyr :options="{ clickToPlay: false, controls: [] }">
                    <video :src="row.item.attachment" format="audio/mp4"/>
                  </VuePlyr>
                </div>
                <!-- No @load, ever: v-lazy emits no Vue event and no DOM event. Its only signal is the
                     lazy="loading|loaded|error" attribute, which CSS reads. -->
                <img v-else
                     v-lazy="row.item.attachment"
                     class="v2026-mtm-att__img"
                     width="60"
                     alt="">
              </div>
            </div>
          </div>

          <div v-if="categories == null" class="v2026-mtm-skeleton" aria-hidden="true">
            <div v-for="n in 3" :key="'sk' + n" class="v2026-mtm-skel">
              <span class="v2026-mtm-skel__chip"></span>
              <span class="v2026-mtm-skel__bars">
                <i class="v2026-mtm-skel__bar v2026-mtm-skel__bar--sm"></i>
                <i class="v2026-mtm-skel__bar"></i>
              </span>
            </div>
          </div>
          <div v-else-if="!rowCount" class="v2026-mtm-empty">
            {{ query ? 'ไม่พบคำค้นหา' : 'ยังไม่มีคีย์ลัด' }}
          </div>
        </div>
      </template>
    </OverlayCard>
  </div>
</template>

<script>
import OverlayCard from '@/components/coversation/OverlayCard'

const SETTINGS_ICON = require('@/assets/icons2026/settings.svg')

export default {
  name: 'message-template-menu-v2026',
  components: {
    OverlayCard
  },
  props: {
    // Straight out of Vuex. null = still loading (skeleton), [] = genuinely empty.
    categories: {
      type: Array,
      default: null
    },
    conversationId: {
      type: [Number, String],
      default: null
    }
  },
  data () {
    return {
      open: false,
      query: '',
      activeIndex: -1,
      triggerEl: null,
      settingsIcon: SETTINGS_ICON
    }
  },
  computed: {
    panelId () {
      return 'v2026-mtm-card'
    },
    listId () {
      return 'v2026-mtm-list'
    },
    groups () {
      const out = []
      let idx = 0
      ;(this.categories || []).forEach((category, ci) => {
        const templates = (category && category.message_template) || []
        if (!templates.length) return
        out.push({
          key: String(category.id) + '_' + ci,
          name: category.name,
          rows: templates.map(item => {
            const row = {
              key: String(category.id) + '_' + String(item.id),
              id: this.panelId + '-opt-' + category.id + '-' + item.id,
              idx: idx++,
              item,
              label: this.rowLabel(item)
            }
            return row
          })
        })
      })
      return out
    },
    flatRows () {
      return this.groups.reduce((all, g) => all.concat(g.rows), [])
    },
    rowCount () {
      return this.flatRows.length
    },
    activeOptionId () {
      const row = this.flatRows[this.activeIndex]
      return row ? row.id : null
    },
    // Signature of everything the slot renders. Anything left out of it freezes on screen.
    renderKey () {
      return this.query +
        '#' + (this.open ? 1 : 0) +
        '#' + this.activeIndex +
        '#' + this.groups.map(g => g.name + '>' + g.rows.map(r => r.item.id).join(',')).join(';')
    }
  },
  watch: {
    conversationId () {
      this.hide()
    },
    query () {
      if (!this.open) return
      this.activeIndex = this.rowCount ? 0 : -1
      this.$nextTick(() => {
        if (this.$refs.list) this.$refs.list.scrollTop = 0
      })
    },
    rowCount (n) {
      if (!this.open) return
      if (!n) this.activeIndex = -1
      else if (this.activeIndex > n - 1) this.activeIndex = n - 1
      else if (this.activeIndex < 0) this.activeIndex = 0
    }
  },
  mounted () {
    this.triggerEl = this.$refs.triggerBtn || null
  },
  methods: {
    getFileExtension (url) {
      return url.split(/[#?]/)[0].split('.').pop().trim()
    },
    rowLabel (item) {
      const shortText = this.$options.filters.shortText
      let label = 'คีย์ลัด /' + item.key
      if (item.topic) label += ' ' + item.topic
      if (item.type === 'image') label += ' รูปภาพ ' + ((item.images && item.images.length) || 0) + ' รูป'
      else if (item.message) label += ' ' + shortText(item.message, 70)
      if (item.attachment) label += ' มีไฟล์แนบ'
      return label
    },
    show () {
      if (this.open) return
      this.open = true
      this.$emit('open')
      this.activeIndex = this.rowCount ? 0 : -1
      this.$nextTick(() => {
        if (this.$refs.searchInput) this.$refs.searchInput.focus()
        this.scrollActiveIntoView()
      })
    },
    // Same shape as b-dropdown.hide(refocus), so the frozen selectMessageTemplate call site
    // still works. The query is deliberately NOT cleared: filtering is server-side, so an
    // empty box above a still-filtered list would be lying.
    hide (returnFocus = false) {
      const wasOpen = this.open
      this.open = false
      this.activeIndex = -1
      if (wasOpen) this.$emit('close')
      if (returnFocus) {
        this.$nextTick(() => {
          if (this.$refs.triggerBtn) this.$refs.triggerBtn.focus()
        })
      }
    },
    toggle () {
      if (this.open) this.hide(true)
      else this.show()
    },
    scrollActiveIntoView () {
      const list = this.$refs.list
      if (!list || this.activeIndex < 0) return
      const el = list.querySelector('[data-idx="' + this.activeIndex + '"]')
      if (el) el.scrollIntoView({ block: 'nearest' })
    },
    moveTo (index) {
      if (!this.rowCount) {
        this.activeIndex = -1
        return
      }
      this.activeIndex = Math.max(0, Math.min(this.rowCount - 1, index))
      this.$nextTick(() => this.scrollActiveIntoView())
    },
    onTriggerArrow (where) {
      const target = () => this.moveTo(where === 'last' ? this.rowCount - 1 : 0)
      if (!this.open) {
        this.show()
        this.$nextTick(target)
        return
      }
      target()
    },
    onSearchInput () {
      this.$emit('search', this.query)
    },
    onSearchKeydown (e) {
      const key = e.key
      if (key === 'ArrowDown') {
        e.preventDefault()
        this.moveTo(this.activeIndex + 1)
      } else if (key === 'ArrowUp') {
        e.preventDefault()
        this.moveTo(this.activeIndex - 1)
      } else if (key === 'Home' || key === 'End') {
        // With text typed, Home/End belong to the caret.
        if (this.query) return
        e.preventDefault()
        this.moveTo(key === 'Home' ? 0 : this.rowCount - 1)
      } else if (key === 'Enter') {
        e.preventDefault()
        const row = this.flatRows[this.activeIndex]
        if (row) this.onPick(row)
      }
    },
    onListMousedown (e) {
      const list = this.$refs.list
      if (!list) return
      // Let a press on the scrollbar gutter through; everything else keeps the caret in search.
      if (e.target === list && e.offsetX >= list.clientWidth) return
      e.preventDefault()
    },
    onEsc (e) {
      if (!this.open) return
      e.stopPropagation()
      this.hide(true)
    },
    onFocusOut (e) {
      if (!this.open) return
      // null relatedTarget = the window lost focus (Alt-Tab). Outside clicks are OverlayCard's job.
      if (!e.relatedTarget) return
      if (this.$el.contains(e.relatedTarget)) return
      this.hide()
    },
    onPick (row) {
      if (!this.open) return
      this.$emit('select', row.item)
    },
    onScroll (e) {
      const el = e.target
      if (el.scrollTop + el.clientHeight >= el.scrollHeight) this.$emit('scroll-end', e)
    }
  }
}
</script>

<style scoped>
.v2026-mtm-anchor {
  position: relative;
  display: inline-flex;
  vertical-align: middle;
  flex-shrink: 0;
}

.v2026-mtm-anchor .v2026-keylad {
  border-radius: 0.25rem;
  font-family: inherit;
  font-weight: 400;
  line-height: 1.5;
  text-align: center;
  vertical-align: middle;
  cursor: pointer;
  -webkit-appearance: none;
     -moz-appearance: none;
          appearance: none;
  background-image: none;
  transition: background-color 150ms ease, color 150ms ease;
}

.v2026-mtm-anchor .v2026-keylad:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}

.v2026-chev {
  font-size: 10px;
  color: var(--v2026-text-muted, #98A2B3);
}

.v2026-mtm-anchor .v2026-mtm-pos {
  bottom: calc(100% + var(--v2026-sp-2, 8px));
  left: 0;
  width: 404px;
  min-width: 0;
  max-width: calc(100vw - 24px);
  max-height: min(60vh, 520px);
  padding: var(--v2026-sp-2, 8px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.v2026-mtm-head {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  margin-bottom: var(--v2026-sp-2, 8px);
}

.v2026-mtm-search {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #F8FAFC;
  border: 1px solid var(--v2026-border, #E4E7EC);
  border-radius: 10px;
}

.v2026-mtm-search:focus-within {
  border-color: var(--v2026-color-main, #31C1D7);
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}

.v2026-mtm-search__ico {
  flex: 0 0 auto;
  font-size: 14px;
  color: var(--v2026-text-muted, #98A2B3);
}

.v2026-mtm-search__input {
  flex: 1 1 auto;
  min-width: 0;
  border: 0;
  outline: none;
  background: transparent;
  color: var(--v2026-text, #101828);
  font-size: var(--v2026-fs-body, 14px);
}

.v2026-mtm-search__input::placeholder {
  color: var(--v2026-text-muted, #98A2B3);
}

.v2026-mtm-setting {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: var(--v2026-sp-2, 8px);
  border-radius: 6px;
  color: var(--v2026-text-secondary, #667085);
  font-size: var(--v2026-fs-ctrl, 13px);
  text-decoration: none;
  white-space: nowrap;
}

.v2026-mtm-setting:hover {
  color: var(--v2026-text, #101828);
  text-decoration: underline;
}

.v2026-mtm-setting:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(49, 193, 215, 0.30);
}

.v2026-mtm-setting__ico {
  width: 14px;
  height: 14px;
  display: inline-block;
  flex: 0 0 auto;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
  background-color: currentColor;
}

.v2026-mtm-list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
  gap: 2px;
  outline: none;
}

.v2026-mtm-list::-webkit-scrollbar {
  width: 6px;
}

.v2026-mtm-list::-webkit-scrollbar-thumb {
  background: var(--v2026-color-main, #31C1D7);
  border-radius: 3px;
}

.v2026-mtm-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.v2026-mtm-group + .v2026-mtm-group {
  margin-top: 6px;
}

.v2026-mtm-header {
  padding: 8px 10px 4px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--v2026-text-muted, #98A2B3);
  cursor: default;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.v2026-mtm-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  grid-template-areas:
    "key topic att"
    "key body  att";
  column-gap: 8px;
  row-gap: 2px;
  align-items: start;
  padding: 8px 10px;
  min-height: 40px;
  border-radius: var(--v2026-r-btn, 10px);
  background: var(--v2026-surface, #FFFFFF);
  color: #374151;
  font-size: var(--v2026-fs-ctrl, 13px);
  cursor: pointer;
  transition: background-color 150ms ease;
}

.v2026-mtm-row:hover {
  background: var(--v2026-surface-hover, #E9ECF1);
}

.v2026-mtm-row.is-active {
  background: var(--v2026-color-main-soft, #31C1D714);
  box-shadow: inset 0 0 0 2px rgba(49, 193, 215, 0.55);
}

.v2026-mtm-row:active {
  background: #DDE1E7;
}

.v2026-mtm-key {
  grid-area: key;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 20px;
  padding: 0 6px;
  border: 1px solid var(--v2026-border, #E4E7EC);
  border-radius: 6px;
  background: var(--v2026-surface-sunken, #F2F4F7);
  color: var(--v2026-text-secondary, #667085);
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.v2026-mtm-row:hover .v2026-mtm-key,
.v2026-mtm-row.is-active .v2026-mtm-key {
  background: var(--v2026-color-main-soft, #31C1D714);
  border-color: rgba(49, 193, 215, 0.35);
}

.v2026-mtm-topic {
  grid-area: topic;
  min-width: 0;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  color: var(--v2026-text-secondary, #667085);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.v2026-mtm-body {
  grid-area: body;
  min-width: 0;
  line-height: 20px;
  word-break: break-word;
}

.v2026-mtm-strip {
  display: flex;
  gap: 8px;
  width: 100%;
  overflow-x: auto;
  overscroll-behavior-x: contain;
}

.v2026-mtm-strip__img {
  flex: 0 0 auto;
  height: 50px;
  width: auto;
  border-radius: 6px;
}

.v2026-mtm-att {
  grid-area: att;
  flex: 0 0 auto;
}

.v2026-mtm-att__img {
  display: block;
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
  background: var(--v2026-surface-sunken, #F2F4F7);
}

.v2026-mtm-att__img[lazy="error"] {
  opacity: 0.4;
}

.v2026-mtm-att__video {
  width: 120px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  background: var(--v2026-surface-sunken, #F2F4F7);
}

.v2026-mtm-att__video ::v-deep .plyr {
  width: 100%;
  height: 100%;
}

.v2026-mtm-att__video ::v-deep .plyr video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.v2026-mtm-empty {
  padding: var(--v2026-sp-3, 12px);
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 12px;
  color: var(--v2026-text-muted, #98A2B3);
}

.v2026-mtm-skel {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 10px;
}

.v2026-mtm-skel__chip {
  flex: 0 0 auto;
  width: 30px;
  height: 20px;
  border-radius: 6px;
  background: var(--v2026-surface-sunken, #F2F4F7);
}

.v2026-mtm-skel__bars {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 4px;
}

.v2026-mtm-skel__bar {
  display: block;
  width: 90%;
  height: 10px;
  border-radius: 4px;
  background: var(--v2026-surface-sunken, #F2F4F7);
}

.v2026-mtm-skel__bar--sm {
  width: 60%;
}

@media (prefers-reduced-motion: reduce) {
  .v2026-mtm-anchor .v2026-keylad,
  .v2026-mtm-row {
    transition: none;
  }
}
</style>
